/**
 * Direction Plugin for Lexical
 * Auto-detects and applies RTL/LTR direction based on text content
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $getSelection,
  $isRangeSelection,
} from 'lexical'
import { useEffect } from 'react'

import { logger } from '../../../../utils/logger'
import { detectParagraphDirection } from '../../editor.utils'
import { $isCustomHeadingNode } from '../nodes/CustomHeadingNode'
import { $isCustomParagraphNode } from '../nodes/CustomParagraphNode'

export interface DirectionPluginProps {
  defaultDirection?: 'ltr' | 'rtl'
  autoDetect?: boolean
}

export function DirectionPlugin({ 
  defaultDirection = 'ltr', 
  autoDetect = true 
}: DirectionPluginProps) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    if (!autoDetect) {return}

    return editor.registerUpdateListener(({ editorState, dirtyElements }) => {
      // Only process if there are dirty elements
      if (dirtyElements.size === 0) {return}

      editorState.read(() => {
        const selection = $getSelection()
        if (!$isRangeSelection(selection)) {return}

        const anchorNode = selection.anchor.getNode()
        // Use getTopLevelElement (without throw) to handle root node case (e.g., after delete all)
        const topLevelElement = anchorNode.getTopLevelElement()
        if (!topLevelElement) {return}

        // Only auto-detect for paragraph and heading nodes
        if (!$isCustomParagraphNode(topLevelElement) && !$isCustomHeadingNode(topLevelElement)) {
          return
        }

        // Get text content of the block
        const textContent = topLevelElement.getTextContent()
        if (!textContent || textContent.trim().length === 0) {return}

        // Detect direction
        const detectedDir = detectParagraphDirection(textContent.trim())
        if (!detectedDir) {return}

        // Get current direction
        const currentDir = topLevelElement.getDir?.() || defaultDirection

        // Only update if direction changed
        if (detectedDir !== currentDir) {
          editor.update(() => {
            if ($isCustomParagraphNode(topLevelElement)) {
              topLevelElement.setDir(detectedDir)
            } else if ($isCustomHeadingNode(topLevelElement)) {
              topLevelElement.setDir(detectedDir)
            }
            logger.debug(`Direction auto-detected: ${currentDir} → ${detectedDir}`)
          })
        }
      })
    })
  }, [editor, autoDetect, defaultDirection])

  return null
}

export default DirectionPlugin

